import {
  Component,
  Input,
  OnInit,
  OnChanges,
  AfterViewInit,
  ViewChild,
  Output,
  EventEmitter,
  SimpleChanges,
  TemplateRef
} from '@angular/core';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SelectionModel } from '@angular/cdk/collections';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { FlatTreeControl } from '@angular/cdk/tree';
import { MatTreeFlatDataSource, MatTreeFlattener } from '@angular/material/tree';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

// Interface para nós da árvore
interface TreeNode {
  [key: string]: any;
  children?: TreeNode[];
  childrenCount?: number;
  hasChildren?: boolean;
  childrenLoaded?: boolean;
  loading?: boolean;
}

// Interface para nós achatados (flat nodes)
interface FlatNode {
  expandable: boolean;
  level: number;
  loading: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.css']
})
export class TableComponent implements OnInit, OnChanges, AfterViewInit {
  @Input() actions: Array<{ name: string; method: (id: number) => void,  show?: (element: any) => boolean; }> = [];
  @Input() headerActions: Array<{ name: string; method: (selection: any[]) => void }> = [];
  @Input() dataSource = new MatTableDataSource<any>([]);
  @Input() totalItems: number = 0;
  @Input() pageSize: number = 30;
  @Input() pageIndex: number = 0;
  @Input() columns: Array<{
    def: string;
    header: string;
    minWidth?: string;
    cellTemplate?: TemplateRef<any>;
  }> = [];
  @Input() onDuplicar: (id: number) => void;
  @Input() onEditar: (id: number) => void;
  @Input() onExcluir: (id: number) => void;
  @Input() enableSelection = false;
  @Input() enablePagination = true;
  @Input() itemIdProperty = 'id';
  @Input() BloquearEdicao: boolean = false; // Novo input para controlar permissão de edição

  /** `def` das colunas que não devem aparecer na grade. Quem escolhe é o componente pai. */
  @Input() hiddenColumns: string[] = [];

  // ===== NOVOS INPUTS PARA MODO ÁRVORE =====
  @Input() treeMode: boolean = false; // Ativa o modo árvore
  @Input() treeLoadChildren: (node: any) => Observable<any[]>; // Função para carregar filhos
  @Input() treeIdProperty: string = 'id'; // Propriedade que identifica o ID do nó
  @Input() treeParentIdProperty: string = 'paiId'; // Propriedade que identifica o ID do pai
  @Input() treeHasChildrenProperty: string = 'hasChildren'; // Propriedade que indica se tem filhos

  @Output() pageChange = new EventEmitter<PageEvent>();
  @Output() selectionChange = new EventEmitter<any[]>();

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  displayedColumns: string[] = [];
  selection = new SelectionModel<any>(true, []);
  isSmallScreen = false;

  // ===== PROPRIEDADES PARA MODO ÁRVORE =====
  treeDataSource: MatTreeFlatDataSource<TreeNode, FlatNode>;
  treeControl: FlatTreeControl<FlatNode>;
  treeFlattener: MatTreeFlattener<TreeNode, FlatNode>;
  flatNodeMap = new Map<FlatNode, TreeNode>();
  nestedNodeMap = new Map<TreeNode, FlatNode>();
  isLoadingSelectAll: boolean = false; // Indica se está expandindo tudo para selecionar

  ngOnInit(): void {

    this.atualizarColunasExibidas();

    if (this.treeMode) {
      this.initializeTreeMode();
    }

    this.isSmallScreen = window.innerWidth < 768;

    this.selection.changed.subscribe(() => {
      this.selectionChange.emit(this.getSelectedRows());
    });
  }

  ngOnChanges(changes: SimpleChanges): void {

    if (changes.hiddenColumns && !changes.hiddenColumns.firstChange) {
      this.atualizarColunasExibidas();
    }

    if (changes.dataSource && !changes.dataSource.firstChange) {

      if (this.treeMode && this.treeDataSource) {

        this.treeDataSource.data =
          changes.dataSource.currentValue.data || [];

      } else {

        this.dataSource.data =
          changes.dataSource.currentValue.data || [];

        // reaplica ordenação
        this.dataSource.sort = this.sort;

        // força refresh interno
        this.dataSource._updateChangeSubscription();
      }
    }

    if (this.paginator && (changes.totalItems || changes.dataSource)) {
      this.paginator.length = this.totalItems;
    }
  }

  private atualizarColunasExibidas(): void {

    const ocultas = this.hiddenColumns || [];

    const baseColumns = this.columns
      .filter(column => !ocultas.includes(column.def))
      .map(column => column.def);

    // Se estiver em modo árvore, adiciona coluna de expansão
    if (this.treeMode) {
      this.displayedColumns = this.enableSelection
        ? ['select', 'expand', ...baseColumns]
        : ['expand', ...baseColumns];
    } else {
      this.displayedColumns = this.enableSelection
        ? ['select', ...baseColumns]
        : baseColumns;
    }
  }

  ngAfterViewInit(): void {

    if (!this.treeMode) {

      this.dataSource.sort = this.sort;

      this.dataSource._updateChangeSubscription();
    }

    if (this.paginator) {
      this.paginator.length = this.totalItems;
    }

  }

  // ===== MÉTODOS PARA MODO ÁRVORE =====

  private initializeTreeMode(): void {
    // Transformer: converte TreeNode em FlatNode
    const transformer = (node: TreeNode, level: number): FlatNode => {
      const existingNode = this.nestedNodeMap.get(node);
      const flatNode: FlatNode = existingNode || {
        expandable: !!node[this.treeHasChildrenProperty],
        level: level,
        loading: false
      };

      // Copia todas as propriedades do nó original para o flatNode
      Object.keys(node).forEach(key => {
        if (key !== 'children' && key !== 'childrenLoaded') {
          flatNode[key] = node[key];
        }
      });

      flatNode.level = level;
      flatNode.expandable = !!node[this.treeHasChildrenProperty];

      this.flatNodeMap.set(flatNode, node);
      this.nestedNodeMap.set(node, flatNode);

      return flatNode;
    };

    this.treeFlattener = new MatTreeFlattener(
      transformer,
      node => node.level,
      node => node.expandable,
      node => node.children
    );

    this.treeControl = new FlatTreeControl<FlatNode>(
      node => node.level,
      node => node.expandable
    );

    this.treeDataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);
    this.treeDataSource.data = this.dataSource.data || [];

    // Monitora expansão/colapso de nós
    this.treeControl.expansionModel.changed.subscribe(change => {
      if ((change as any).added) {
        (change.added as FlatNode[]).forEach(node => this.loadChildren(node));
      }

      if ((change as any).removed) {
        (change.removed as FlatNode[]).forEach(node => {
          node.loading = false;
        });
      }

      // Força atualização da visualização
      this.treeDataSource.data = this.treeDataSource.data;
    });
  }

  toggleTreeNode(node: FlatNode): void {
    node.loading = true;
    this.treeDataSource.data = this.treeDataSource.data;
    this.treeControl.toggle(node);
  }

  private loadChildren(node: FlatNode): void {
    if (this.treeControl.isExpanded(node) && node.expandable) {
      const parentNode = this.flatNodeMap.get(node);

      if (parentNode && !parentNode.childrenLoaded) {
        if (this.treeLoadChildren) {
          // Chama a função fornecida pelo componente pai
          this.treeLoadChildren(parentNode).pipe(
            map(response => response || [])
          ).subscribe(children => {
            parentNode.children = children;
            parentNode.childrenLoaded = true;
            node.loading = false;
            this.treeDataSource.data = this.treeDataSource.data;
          }, error => {
            console.error('Erro ao carregar filhos:', error);
            node.loading = false;
            this.treeDataSource.data = this.treeDataSource.data;
          });
        } else {
          node.loading = false;
          this.treeDataSource.data = this.treeDataSource.data;
        }
      } else {
        node.loading = false;
        this.treeDataSource.data = this.treeDataSource.data;
      }
    }
  }

  hasChild = (_: number, node: FlatNode) => node.expandable;

  /**
   * Retorna a classe CSS baseada no nível do nó na árvore
   */
  getTreeLevelClass(node: FlatNode): string {
    if (!node || node.level === undefined) {
      return '';
    }

    if (node.level <= 5) {
      return `tree-level-${node.level}`;
    } else {
      return 'tree-level-deep';
    }
  }

  /**
   * Constrói o caminho completo do ID (ex: 10.11.13 para netos)
   */
  getFullIdPath(node: FlatNode): string {
    if (!this.treeMode || !node) {
      return node[this.treeIdProperty] || '';
    }

    const path: string[] = [];
    let currentNode: TreeNode | undefined = this.flatNodeMap.get(node);

    // Constrói o caminho de baixo para cima
    while (currentNode) {
      const id = currentNode[this.treeIdProperty];
      if (id !== null && id !== undefined) {
        path.unshift(id.toString());
      }

      // Busca o pai
      const parentId = currentNode[this.treeParentIdProperty];
      if (parentId === null || parentId === undefined) {
        break; // Chegou na raiz
      }

      // Encontra o nó pai
      currentNode = this.findNodeById(parentId);
    }

    return path.join('.');
  }

  /**
   * Busca um nó pelo ID na estrutura de dados
   */
  private findNodeById(id: any): TreeNode | undefined {
    const searchInNodes = (nodes: TreeNode[]): TreeNode | undefined => {
      for (const node of nodes) {
        if (node[this.treeIdProperty] === id) {
          return node;
        }
        if (node.children && node.children.length > 0) {
          const found = searchInNodes(node.children);
          if (found) return found;
        }
      }
      return undefined;
    };

    return searchInNodes(this.treeDataSource?.data || []);
  }

  // ===== MÉTODOS EXISTENTES =====

  getVisibleActions(el: any) {
    return this.actions?.filter(a => !a.show || a.show(el));
  }

  isValidDate(value: any): boolean {
    if (!this.isDate(value)) return false;
    const d = new Date(value);
    return !(d.getFullYear() === 1950 && d.getMonth() === 0 && d.getDate() === 1);
  }

  isDate(value: any): boolean {
    if (value instanceof Date && !isNaN(value.getTime())) return true;

    if (typeof value === 'string') {
      const isoDateRegex = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])(T.*)?$/;
      if (!isoDateRegex.test(value)) return false;

      const parsed = Date.parse(value);
      return !isNaN(parsed);
    }

    return false;
  }

  isNumeric(value: any): boolean {
    return value !== null
        && value !== ''
        && !isNaN(parseFloat(value))
        && isFinite(value);
  }

  formatNumber(value: any): string {
    return this.isNumeric(value)
      ? value.toString().replace(/\./g, ',')
      : value;
  }

  getNestedValue(obj: any, path: string): any {
    return path
      .split('.')
      .reduce((current, key) => (current == null ? null : current[key]), obj);
  }

  isAllSelected(): boolean {
    if (this.treeMode) {
      // Em modo árvore, verifica se todos os nós visíveis estão selecionados
      const allNodes = this.treeControl.dataNodes || [];
      return allNodes.length > 0 && this.selection.selected.length === allNodes.length;
    } else {
      const numRows = this.dataSource.data?.length ?? 0;
      return this.selection.selected.length === numRows;
    }
  }

  async masterToggle(): Promise<void> {
    if (this.isAllSelected()) {
      // Se já está tudo selecionado, apenas limpa
      this.selection.clear();
    } else {
      if (this.treeMode) {
        // Em modo árvore, expande tudo recursivamente antes de selecionar
        this.isLoadingSelectAll = true;
        try {
          await this.expandAllAndSelect();
        } catch (error) {
          console.error('Erro ao expandir e selecionar todos:', error);
        } finally {
          this.isLoadingSelectAll = false;
        }
      } else {
        // Em modo tabela normal, seleciona apenas os visíveis
        this.dataSource.data.forEach(row => this.selection.select(row));
      }
    }
  }

  /**
   * Expande recursivamente todos os nós da árvore e depois seleciona todos
   */
  private async expandAllAndSelect(): Promise<void> {
    // Coleta todos os nós raiz (nível 0)
    const rootNodes = this.treeControl.dataNodes.filter(node => node.level === 0);

    // Expande recursivamente cada nó raiz
    for (const node of rootNodes) {
      await this.expandNodeRecursively(node);
    }

    // Após expandir tudo, seleciona todos os nós visíveis
    const allNodes = this.treeControl.dataNodes || [];
    allNodes.forEach(node => this.selection.select(node));
  }

  /**
   * Expande um nó e todos os seus descendentes recursivamente
   */
  private async expandNodeRecursively(node: FlatNode): Promise<void> {
    if (!node.expandable) {
      return; // Nó folha, não tem filhos
    }

    // Se o nó não está expandido, expande
    if (!this.treeControl.isExpanded(node)) {
      this.treeControl.expand(node);

      // Aguarda o carregamento dos filhos
      const parentNode = this.flatNodeMap.get(node);
      if (parentNode && !parentNode.childrenLoaded && this.treeLoadChildren) {
        await new Promise<void>((resolve, reject) => {
          this.treeLoadChildren(parentNode).pipe(
            map(response => response || [])
          ).subscribe(
            children => {
              parentNode.children = children;
              parentNode.childrenLoaded = true;
              this.treeDataSource.data = this.treeDataSource.data;
              resolve();
            },
            error => {
              console.error('Erro ao carregar filhos durante expansão total:', error);
              reject(error);
            }
          );
        });
      }
    }

    // Após expandir este nó, expande recursivamente todos os seus filhos
    const descendants = this.treeControl.getDescendants(node);
    for (const descendant of descendants) {
      if (descendant.expandable && descendant.level === node.level + 1) {
        // Apenas expande os filhos diretos, a recursão cuidará dos netos
        await this.expandNodeRecursively(descendant);
      }
    }
  }

  toggleRow(row: any): void {
    this.selection.toggle(row);
  }

  getSelectedRows(): any[] {
    return this.selection.selected;
  }

  duplicar(item: any): void {
    const id = item[this.itemIdProperty];
    this.onDuplicar?.(id);
  }

  editar(item: any): void {
    const id = item[this.itemIdProperty];
    this.onEditar?.(id);
  }

  excluir(item: any): void {
    const id = item[this.itemIdProperty];
    this.onExcluir?.(id);
  }

  limparSelecao() {
    this.selection.clear();
  }

  salvarSelecaoAnterior() {
    return this.selection.selected.map(item => item[this.itemIdProperty]);
  }

  restaurarSelecao(idsSelecionados: any[]) {
    const itensParaSelecionar = this.dataSource.data.filter(item =>
      idsSelecionados.includes(item[this.itemIdProperty])
    );
    itensParaSelecionar.forEach(item => this.selection.select(item));
  }
}
